import * as React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Accordion, AccordionSummary, AccordionDetails,
  Chip, Stack, Box, Checkbox, FormControlLabel, DialogContentText, Alert
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export default function DiffReviewDialog({ open, onClose, data }) {
  // data.files: [{ path, resolved_path, diff, new_content }]
  const [files, setFiles] = React.useState([]);
  const [selected, setSelected] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [preview, setPreview] = React.useState(null); // {path, new_content}
  const [preflight, setPreflight] = React.useState(null); // {issues, warnings}
  const [strict, setStrict] = React.useState(true);

  React.useEffect(() => {
    const f = (data?.files || []).map((x) => ({ ...x, status: "pending" }));
    setFiles(f);
    setSelected({});
    setPreflight(null);
  }, [data]);

  const toggle = (path) => setSelected((s) => ({ ...s, [path]: !s[path] }));
  const anySelected = Object.values(selected).some(Boolean);

  const _payloadFor = (targets) => ({
    strict,
    files: targets.map((f) => ({
      path: f.path,
      resolved_path: f.resolved_path,
      new_content: f.new_content,
      diff: f.diff
    }))
  });

  const preflightAll = async (targets) => {
    const payload = { ..._payloadFor(targets), validate_only: true };
    const res = await fetch("/api/patch/apply", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then((r) => r.json());
    return res;
  };

  const applyAllInternal = async (targets) => {
    const payload = _payloadFor(targets);
    const res = await fetch("/api/patch/apply", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then((r) => r.json());
    return res;
  };

  const markStatusForTargets = (targets, status, errMsg) => {
    setFiles((arr) =>
      arr.map((f) =>
        targets.some((t) => t.path === f.path)
          ? { ...f, status, errMsg }
          : f
      )
    );
  };

  const applySelected = async () => {
    const targets = files.filter((f) => selected[f.path]);
    if (targets.length === 0) return;

    setBusy(true);
    setPreflight(null);
    try {
      // 1) Preflight
      const pre = await preflightAll(targets);
      if (!pre.ok) {
        setPreflight({ issues: pre.issues || [], warnings: pre.warnings || [], phase: pre.phase || "preflight" });
        markStatusForTargets(targets, "error", "Preflight failed");
        return;
      }
      if ((pre.warnings || []).length && strict) {
        setPreflight({ issues: [], warnings: pre.warnings, phase: "preflight" });
        markStatusForTargets(targets, "pending");
        return;
      }

      // 2) Apply (atomic on backend)
      const res = await applyAllInternal(targets);
      if (!res.ok) {
        setPreflight({ issues: [res.error || "apply failed"], warnings: res.warnings || [], phase: "apply" });
        markStatusForTargets(targets, "error", res.error || "apply failed");
        return;
      }
      markStatusForTargets(targets, "applied");
    } catch (e) {
      setPreflight({ issues: [String(e)], warnings: [], phase: "client" });
      markStatusForTargets(targets, "error", String(e));
    } finally {
      setBusy(false);
    }
  };

  const applyAll = async () => {
    const targets = files;
    if (targets.length === 0) return;

    setBusy(true);
    setPreflight(null);
    try {
      const pre = await preflightAll(targets);
      if (!pre.ok) {
        setPreflight({ issues: pre.issues || [], warnings: pre.warnings || [], phase: pre.phase || "preflight" });
        setFiles((arr) => arr.map((f) => ({ ...f, status: "error" })));
        return;
      }
      if ((pre.warnings || []).length && strict) {
        setPreflight({ issues: [], warnings: pre.warnings, phase: "preflight" });
        return;
      }

      const res = await applyAllInternal(targets);
      if (!res.ok) {
        setPreflight({ issues: [res.error || "apply failed"], warnings: res.warnings || [], phase: "apply" });
        setFiles((arr) => arr.map((f) => ({ ...f, status: "error", errMsg: res.error || "apply failed" })));
        return;
      }
      setFiles((arr) => arr.map((f) => ({ ...f, status: "applied" })));
    } catch (e) {
      setPreflight({ issues: [String(e)], warnings: [], phase: "client" });
      setFiles((arr) => arr.map((f) => ({ ...f, status: "error", errMsg: String(e) })));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>GPT Proposed Diffs</DialogTitle>
      <DialogContent dividers>
        {(data?.notes?.length ?? 0) > 0 && (
          <>
            <Typography variant="subtitle1">Notes</Typography>
            <ul style={{ marginTop: 4 }}>
              {data.notes.map((n, i) => (
                <li key={i}><Typography variant="body2">{n}</Typography></li>
              ))}
            </ul>
          </>
        )}

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <FormControlLabel
            control={<Checkbox checked={strict} onChange={(e) => setStrict(e.target.checked)} />}
            label="Strict mode (fail on warnings)"
          />
          {preflight?.phase && (
            <Chip size="small" label={`phase: ${preflight.phase}`} />
          )}
        </Stack>

        {preflight?.issues?.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Preflight issues</Typography>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {preflight.issues.map((m, i) => <li key={i}><Typography variant="body2">{m}</Typography></li>)}
            </ul>
          </Alert>
        )}

        {preflight?.warnings?.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Warnings</Typography>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {preflight.warnings.map((m, i) => <li key={i}><Typography variant="body2">{m}</Typography></li>)}
            </ul>
            {!strict && (
              <Typography variant="caption" color="text.secondary">
                Strict mode is off, you may proceed.
              </Typography>
            )}
          </Alert>
        )}

        {files.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No diffs returned.</Typography>
        ) : (
          files.map((f, i) => (
            <Accordion key={f.path} defaultExpanded={i === 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%", justifyContent: "space-between" }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FormControlLabel
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                      control={<Checkbox checked={!!selected[f.path]} onChange={() => toggle(f.path)} />}
                      label={<Typography variant="subtitle2" sx={{ fontFamily: "monospace" }}>{f.path}</Typography>}
                    />
                  </Box>
                  <Chip size="small" label={f.status} color={f.status === "applied" ? "success" : f.status === "error" ? "error" : "default"} />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="caption" color="text.secondary">{f.resolved_path}</Typography>
                <pre style={{ whiteSpace: "pre-wrap", marginTop: 8, maxHeight: 280, overflow: "auto" }}>{f.diff || "[no diff provided]"}</pre>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" onClick={() => setPreview({ path: f.path, new_content: f.new_content })}>
                    Review new content
                  </Button>
                </Stack>
                {f.status === "error" && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {f.errMsg || "Unknown error"}
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        <Button variant="outlined" onClick={applySelected} disabled={busy || !anySelected}>Preflight + Apply selected</Button>
        <Button variant="contained" onClick={applyAll} disabled={busy || files.length === 0}>Preflight + Apply all</Button>
      </DialogActions>

      <Dialog open={!!preview} onClose={() => setPreview(null)} fullWidth maxWidth="lg">
        <DialogTitle>New content: {preview?.path}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div">
            <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{preview?.new_content || ""}</pre>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
